import React, { useState } from 'react';
import styled from 'styled-components';
import { IoAdd, IoClose, IoPinSharp, IoCreateOutline, IoTrashOutline } from 'react-icons/io5';
import { useNotices } from '../../../context/NoticeContext';
import { format } from 'date-fns';

const CATEGORIES = ['전체', '제품', '일정', '일반'];

const AdminNoticePage = () => {
  const { notices, addNotice, updateNotice, removeNotice, togglePin } = useNotices();
  const [category, setCategory] = useState('전체');
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ title: '', category: '일반', content: '' });
  const [submitting, setSubmitting] = useState(false);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 20;

  const filtered = notices.filter(n => category === '전체' || n.category === category);
  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const openCreate = () => {
    setEditId(null);
    setForm({ title: '', category: '일반', content: '' });
    setShowModal(true);
  };

  const openEdit = (n) => {
    setEditId(n.id);
    setForm({ title: n.title, category: n.category || '일반', content: n.content || '' });
    setShowModal(true);
  };

  const handleSubmit = async () => {
    if (!form.title.trim()) return;
    setSubmitting(true);
    try {
      if (editId) {
        await updateNotice(editId, { title: form.title.trim(), category: form.category, content: form.content.trim() });
      } else {
        await addNotice({ title: form.title.trim(), category: form.category, content: form.content.trim() });
      }
      setShowModal(false);
    } catch (err) {
      alert('저장에 실패했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('삭제하시겠습니까?')) return;
    await removeNotice(id);
  };

  const formatDate = (ts) => {
    if (!ts?.toDate) return '';
    return format(ts.toDate(), 'yyyy-MM-dd HH:mm');
  };

  return (
    <Container>
      <PageHeader>
        <AddBtn onClick={openCreate}><IoAdd size={18} /> 공지 작성</AddBtn>
      </PageHeader>

      <TabRow>
        {CATEGORIES.map(c => (
          <Tab key={c} $active={category === c} onClick={() => { setCategory(c); setPage(1); }}>{c}</Tab>
        ))}
      </TabRow>

      <TableWrap>
        <Table>
          <thead>
            <Tr><Th style={{ width: 50 }}></Th><Th>제목</Th><Th style={{ width: 80 }}>분류</Th><Th style={{ width: 140 }}>작성시간</Th><Th style={{ width: 100 }}>작성자</Th><Th style={{ width: 180 }}>액션</Th></Tr>
          </thead>
          <tbody>
            {paged.map(n => (
              <Tr key={n.id}>
                <Td>{n.pinned && <PinBadge>고정됨</PinBadge>}</Td>
                <Td>
                  <NoticeTitle>{n.title}</NoticeTitle>
                  <NoticePreview>{(n.content || '').slice(0, 60)}{n.content?.length > 60 ? '...' : ''}</NoticePreview>
                </Td>
                <Td><CatBadge>{n.category || '일반'}</CatBadge></Td>
                <Td>{formatDate(n.createdAt)}</Td>
                <Td>{n.author || '관리자'}</Td>
                <Td>
                  <Actions>
                    <ActTextBtn $pin={n.pinned} onClick={() => togglePin(n.id, n.pinned)}>{n.pinned ? '고정해제' : '고정'}</ActTextBtn>
                    <ActTextBtn onClick={() => openEdit(n)}>수정</ActTextBtn>
                    <ActTextBtn $del onClick={() => handleDelete(n.id)}>삭제</ActTextBtn>
                  </Actions>
                </Td>
              </Tr>
            ))}
          </tbody>
        </Table>
        {filtered.length === 0 && <Empty>공지가 없습니다</Empty>}
      </TableWrap>

      {totalPages > 1 && (
        <Pagination>
          <PageBtn disabled={page === 1} onClick={() => setPage(1)}>«</PageBtn>
          <PageBtn disabled={page === 1} onClick={() => setPage(p => p - 1)}>‹</PageBtn>
          {Array.from({ length: totalPages }, (_, i) => i + 1)
            .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 2)
            .reduce((acc, p, i, arr) => { if (i > 0 && p - arr[i - 1] > 1) acc.push('...'); acc.push(p); return acc; }, [])
            .map((p, i) => p === '...' ? <PageDots key={`dots-${i}`}>…</PageDots> : <PageNum key={p} $active={page === p} onClick={() => setPage(p)}>{p}</PageNum>)}
          <PageBtn disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>›</PageBtn>
          <PageBtn disabled={page === totalPages} onClick={() => setPage(totalPages)}>»</PageBtn>
          <PageInfo>{filtered.length}건 중 {(page - 1) * PAGE_SIZE + 1}-{Math.min(page * PAGE_SIZE, filtered.length)}</PageInfo>
        </Pagination>
      )}

      {showModal && (
        <Overlay onClick={() => setShowModal(false)}>
          <Modal onClick={e => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>{editId ? '공지 수정' : '공지 작성'}</ModalTitle>
              <CloseBtn onClick={() => setShowModal(false)}><IoClose size={22} /></CloseBtn>
            </ModalHeader>
            <ModalBody>
              <FormGroup>
                <FormLabel>제목</FormLabel>
                <FormInput value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="공지 제목" />
              </FormGroup>
              <FormGroup>
                <FormLabel>분류</FormLabel>
                <FormSelect value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}>
                  {CATEGORIES.filter(c => c !== '전체').map(c => <option key={c} value={c}>{c}</option>)}
                </FormSelect>
              </FormGroup>
              <FormGroup>
                <FormLabel>내용</FormLabel>
                <FormTextarea rows={5} value={form.content} onChange={e => setForm({ ...form, content: e.target.value })} placeholder="공지 내용" />
              </FormGroup>
            </ModalBody>
            <ModalFooter>
              <CancelBtn onClick={() => setShowModal(false)}>취소</CancelBtn>
              <SubmitBtn onClick={handleSubmit} disabled={submitting}>{submitting ? '저장 중...' : editId ? '수정' : '등록'}</SubmitBtn>
            </ModalFooter>
          </Modal>
        </Overlay>
      )}
    </Container>
  );
};

export default AdminNoticePage;

const Pagination = styled.div`display: flex; align-items: center; justify-content: center; gap: 4px; padding: 20px 0 8px;`;
const PageBtn = styled.button`
  width: 32px; height: 32px; border-radius: 8px; font-size: 14px;
  background: #fff; color: #666; border: none;
  display: flex; align-items: center; justify-content: center;
  box-shadow: 0 1px 3px rgba(0,0,0,0.06);
  &:disabled { opacity: 0.3; cursor: default; }
  &:hover:not(:disabled) { background: #F0F0EC; }
`;
const PageNum = styled.button`
  width: 32px; height: 32px; border-radius: 8px; font-size: 13px; font-weight: 500;
  border: none; display: flex; align-items: center; justify-content: center;
  background: ${p => p.$active ? '#4A6741' : '#fff'};
  color: ${p => p.$active ? '#fff' : '#666'};
  box-shadow: ${p => p.$active ? 'none' : '0 1px 3px rgba(0,0,0,0.06)'};
  &:hover { background: ${p => p.$active ? '#3B5534' : '#F0F0EC'}; }
`;
const PageDots = styled.span`font-size: 13px; color: #8C9AA4; padding: 0 4px;`;
const PageInfo = styled.span`font-size: 12px; color: #8C9AA4; margin-left: 12px;`;
const Container = styled.div`padding: 32px;`;
const PageHeader = styled.div`display: flex; justify-content: space-between; align-items: center; margin-bottom: 24px;`;
const PageTitle = styled.h1`font-size: 24px; font-weight: 500; color: #1E2A32;`;
const AddBtn = styled.button`
  display: flex; align-items: center; gap: 6px; padding: 9px 18px; border-radius: 10px;
  background: #fff; color: #1E2A32; font-size: 13px; font-weight: 600;
  border: 1.5px solid #D0D0D0;
  &:hover { background: #F5F5F5; border-color: #999; }
`;

const TabRow = styled.div`display: flex; gap: 6px; margin-bottom: 20px;`;
const Tab = styled.button`
  padding: 8px 16px; border-radius: 10px; font-size: 13px; font-weight: 400;
  background: ${p => p.$active ? '#8C9AA4' : '#fff'};
  color: ${p => p.$active ? '#fff' : '#666'};
  border: none;
  box-shadow: ${p => p.$active ? 'none' : '0 1px 3px rgba(0,0,0,0.06)'};
`;

const TableWrap = styled.div`background: #fff; border-radius: 14px; box-shadow: 0 1px 3px rgba(0,0,0,0.05); overflow: hidden;`;
const Table = styled.table`width: 100%; border-collapse: collapse;`;
const Tr = styled.tr`
  position: relative; transition: all 0.15s;
  &:not(:last-child) td { border-bottom: 1px solid #F0F0EC; }
  &:hover td { background: #EDF2EB; }
  &:hover td:first-child { box-shadow: inset 3px 0 0 #4A6741; }
  &:active td { background: #E3EBDF; }
`;
const Th = styled.th`text-align: left; font-size: 13px; color: #8C9AA4; font-weight: 400; padding: 12px 16px; border-bottom: 1px solid #E8E8E4; background: #FAFAF7;`;
const Td = styled.td`font-size: 14px; color: #1E2A32; padding: 14px 16px; font-weight: 300; white-space: nowrap;`;
const Empty = styled.div`text-align: center; padding: 48px; color: #8C9AA4; font-size: 14px;`;
const PinBadge = styled.span`
  font-size: 11px; padding: 2px 8px; border-radius: 4px;
  background: #FFF0ED; color: #E85830; font-weight: 600;
  white-space: nowrap;
`;
const NoticeTitle = styled.div`font-weight: 600; margin-bottom: 2px; font-size: 14px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 600px;`;
const NoticePreview = styled.div`font-size: 13px; color: #8C9AA4; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 600px;`;
const CatBadge = styled.span`font-size: 11px; padding: 3px 10px; border-radius: 10px; background: #EDF2EB; color: #4A6741; font-weight: 400;`;
const Actions = styled.div`display: flex; gap: 6px;`;
const ActTextBtn = styled.button`
  padding: 4px 10px; border-radius: 6px; font-size: 12px; font-weight: 500;
  background: ${p => p.$del ? '#FDF2F2' : p.$pin ? '#FFF0ED' : '#F5F5F0'};
  color: ${p => p.$del ? '#C0392B' : p.$pin ? '#E85830' : '#5A6B75'};
  white-space: nowrap;
  &:hover { opacity: 0.8; }
`;

const Overlay = styled.div`position: fixed; inset: 0; background: rgba(0,0,0,0.4); display: flex; align-items: center; justify-content: center; z-index: 200;`;
const Modal = styled.div`background: #fff; border-radius: 16px; width: 480px;`;
const ModalHeader = styled.div`display: flex; justify-content: space-between; align-items: center; padding: 20px 24px; border-bottom: 1px solid #F0F0EC;`;
const ModalTitle = styled.h3`font-size: 18px; font-weight: 500;`;
const CloseBtn = styled.button`color: #8C9AA4; display: flex;`;
const ModalBody = styled.div`padding: 24px;`;
const ModalFooter = styled.div`display: flex; justify-content: flex-end; gap: 10px; padding: 16px 24px; border-top: 1px solid #F0F0EC;`;
const FormGroup = styled.div`margin-bottom: 16px;`;
const FormLabel = styled.label`display: block; font-size: 13px; font-weight: 400; color: #1E2A32; margin-bottom: 6px;`;
const FormInput = styled.input`width: 100%; padding: 10px 14px; border: none; border-radius: 10px; font-size: 13px; box-shadow: 0 1px 3px rgba(0,0,0,0.06); background: #fff;`;
const FormSelect = styled.select`width: 100%; padding: 10px 14px; border: none; border-radius: 10px; font-size: 13px; background: #fff; box-shadow: 0 1px 3px rgba(0,0,0,0.06);`;
const FormTextarea = styled.textarea`width: 100%; padding: 10px 14px; border: none; border-radius: 10px; font-size: 13px; resize: vertical; font-family: inherit; box-shadow: 0 1px 3px rgba(0,0,0,0.06); background: #fff;`;
const CancelBtn = styled.button`padding: 10px 20px; border-radius: 10px; font-size: 14px; background: #F5F5F0; color: #5A6B75;`;
const SubmitBtn = styled.button`padding: 10px 20px; border-radius: 10px; font-size: 14px; background: #4A6741; color: #fff; font-weight: 400; &:hover { background: #3B5534; } &:disabled { opacity: 0.6; cursor: not-allowed; }`;
